import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Heading from '../../atoms/Heading/Heading'
import PartnerLogo from '../../atoms/PartnerLogo/PartnerLogo'
import CategoryCardV2 from '../../molecules/CategoryCardV2/CategoryCardV2'
import MoreLink from '../../atoms/MoreLink/MoreLink'
import SearchBar from '../../atoms/SearchBar/SearchBar'
import {
  getIntermediateURLWithTag,
  isIpad,
  shuffleArray,
} from '../../../utils/common'
import s from './CategoryListV2.scss'
import useSlider from '../../helpers/NukaSliderWrapper/useSlider'
import useTabs from '../../molecules/Tabs/useTabs'
import fetcher from '../../../utils/fetcher'
import logger from '../../../utils/logger'

const supportedLayouts = [
  'vertical',
  'vertical_2',
  'vertical_3',
  'vertical_4',
  'horizontal',
  'slider',
]

const CategoryListV2 = (props) => {
  const { data = {}, config = {}, isMobile, noShowCallback } = props
  const {
    heading = {},
    items = [],
    linkoutTarget,
    linkout: linkoutURL,
    tabsDataVersion = 1,
    tabs = [],
  } = data
  if (!items.length) {
    noShowCallback()
    return null
  }

  const { text: headingText, type: headingType, size: headingSize } = heading
  const {
    logo = {},
    cardLayout,
    viewMore,
    ga,
    layout: configLayout,
    className = '',
    cardHeight,
    randomizeData,
    tabsPrimaryItemsCount = 5,
    headerBgTheme,
    searchBar,
    ctaText,
    intermediateURLConfig = {},
    validAffiliateList = {},
    noDeals,
    affiliateName,
    useGA4,
    ga4 = {},
  } = config

  const linkout = getIntermediateURLWithTag(
    intermediateURLConfig,
    linkoutURL,
    validAffiliateList.list
  )
  const layout = (() => {
    if (supportedLayouts.indexOf(configLayout) === -1) {
      return isMobile ? 'vertical_2' : 'vertical_4'
    }
    return configLayout
  })()

  const isTabbed = tabs.length > 1

  const sliderSettings = {
    slidesToShow: 1.5,
    slidesToScroll: 1,
    cellSpacing: 24,
    responsive: [
      {
        minWidth: 360,
        maxWidth: 599,
        settings: {
          slidesToShow: cardLayout === 'expand' ? 2 : 3,
          slidesToScroll: cardLayout === 'expand' ? 2 : 3,
          cellSpacing: 32,
        },
      },
      {
        minWidth: 600,
        maxWidth: 665,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        minWidth: 666,
        maxWidth: 991,
        settings: {
          slidesToShow: cardLayout === 'expand' ? 3 : 4,
          slidesToScroll: cardLayout === 'expand' ? 3 : 4,
          cellSpacing: 32,
        },
      },
      {
        minWidth: 992,
        maxWidth: 1167,
        settings: {
          slidesToShow: cardLayout === 'expand' ? 4 : 5,
          slidesToScroll: cardLayout === 'expand' ? 4 : 5,
          cellSpacing: 32,
        },
      },
      {
        minWidth: 1168,
        maxWidth: 2000,
        settings: {
          slidesToShow: cardLayout === 'expand' ? 5 : 6,
          slidesToScroll: cardLayout === 'expand' ? 5 : 6,
          cellSpacing: 32,
        },
      },
    ],
  }
  const isSlider = layout === 'slider'

  const {
    text: partnerLabel,
    imageURL: logoImageURL,
    layout: logoLayout,
  } = logo
  const itemsPerRow =
    layout.indexOf('vertical_') !== -1
      ? parseInt(layout.split('vertical_')[1], 10)
      : 1

  const [finalItemsPerRow, setFinalItemsPerRow] = useState(itemsPerRow)
  const [dataLoading, setDataLoading] = useState(false)

  // #todo: changes to be done along with tabs support
  // use setCategoryItems(randomizeData ? shuffleArray(res.items) : res.items) on tab click
  // eslint-disable-next-line no-unused-vars
  const [categoryItems, setCategoryItems] = useState(
    randomizeData ? shuffleArray(items) : items
  )
  const [tabsPrimaryMenuItemsCount, setTabsPrimaryMenuItemsCount] = useState(
    tabsPrimaryItemsCount
  )

  const SliderModule = useSlider(isSlider)
  const TabModule = useTabs(isTabbed)

  const handleIpad = (ipad) => {
    if (ipad) {
      setFinalItemsPerRow(isSlider ? 1 : 3)
      setTabsPrimaryMenuItemsCount(
        tabsPrimaryMenuItemsCount.ipad || tabsPrimaryMenuItemsCount
      )
    } else {
      setFinalItemsPerRow(itemsPerRow)
      setTabsPrimaryMenuItemsCount(
        tabsPrimaryItemsCount.desktop || tabsPrimaryMenuItemsCount
      )
    }
  }

  useEffect(() => {
    if (itemsPerRow && !isMobile) {
      handleIpad(isIpad(handleIpad))
    }
  }, [])

  const listClass = classNames({
    [s.list]: true,
    aff_e2e: isMobile && layout === 'horizontal',
    [s.col_2_list]: finalItemsPerRow === 2,
    [s.col_3_list]: finalItemsPerRow === 3,
    [s.col_4_list]: finalItemsPerRow === 4,
    [s.horizontal_list]: layout === 'horizontal',
    [s.vertical_list]: layout === 'vertical',
    // [s.slider_list]: isSlider,
  })

  const cardClass = s.card
  const finalItems = [...categoryItems]
  if (finalItems && finalItems.length > 0) {
    const extraItems = finalItems.length % finalItemsPerRow
    if (
      finalItems.length > finalItemsPerRow &&
      extraItems > 0 &&
      finalItemsPerRow > 1
    ) {
      finalItems.splice(-extraItems)
    } // this will remove extraItems from last and change the data
  }

  if (
    finalItems.length > 0 &&
    randomizeData &&
    !Number.isNaN(randomizeData.itemsToShow) &&
    randomizeData.itemsToShow > 0 &&
    randomizeData.itemsToShow < finalItems.length
  ) {
    finalItems.splice(randomizeData.itemsToShow)
  }

  const listContent = finalItems.map((category, index) => (
    <CategoryCardV2
      // eslint-disable-next-line react/no-array-index-key
      key={`${category.categoryName}_${index}`}
      data={category}
      config={{
        ga,
        layout: cardLayout,
        className: cardClass,
        position: index + 1,
        linkoutTarget,
        height: cardHeight,
        ctaText,
        intermediateURLConfig,
        validAffiliateList,
        noDeals,
        affiliateName,
        useGA4,
        ga4,
      }}
      isMobile={isMobile}
    />
  ))

  const fetchData = (dataURL) => {
    if (dataURL) {
      setDataLoading(true)
      fetcher
        .getJSON(dataURL)
        .then((res) => {
          if (res && res.items) {
            setCategoryItems(
              randomizeData ? shuffleArray(res.items) : res.items
            )
            setDataLoading(false)
          }
        })
        // eslint-disable-next-line no-console
        .catch((err) => logger.log(err))
    }
  }

  const onHandleChange = (section) => {
    fetchData(section.dataURL)
  }

  const getSliderWrapper = () => {
    const Slider = SliderModule.default
    return (
      <Slider useGA4={useGA4} ga4={ga4} sliderSettings={sliderSettings}>
        {listContent}
      </Slider>
    )
  }
  const getTabs = () => {
    const { default: Tabs } = TabModule || {}
    return (
      Tabs && (
        <div>
          <Tabs
            tabsData={tabs}
            onChangeCallback={onHandleChange}
            isMobile={isMobile}
            dataversion={tabsDataVersion}
            // #todo : errors should not be committed
            // eslint-disable-next-line css-modules/no-undef-class
            wrapperContainerClass={`${s.tabs} ${isMobile ? 'aff_e2e' : ''}`}
            ga={{
              ...ga,
              action: `${ga.action}_Nav`,
            }}
            affiliateName={affiliateName}
            primaryMenuItemsCount={tabsPrimaryMenuItemsCount}
          />
        </div>
      )
    )
  }

  let finalListContent = listContent

  if (isSlider) {
    finalListContent = SliderModule ? (
      getSliderWrapper()
    ) : (
      <div className="aff_loading" />
    )
  }

  return (
    <div
      className={`${s.container} ${
        dataLoading ? 'aff_loading' : ''
      } ${className} ${isMobile ? '' : s.web}`}
    >
      <div
        className={`${s.header} ${headerBgTheme === 'dark' ? 'aff_bg_d' : ''} ${
          isTabbed && isMobile ? s.mobile_tabed_handling : ''
        }`}
      >
        <Heading
          text={headingText}
          type={headingType}
          size={headingSize}
          url={!viewMore && linkout ? linkout : ''}
          ga={ga}
          useGA4={useGA4}
          ga4={ga4}
          className={s.heading}
          affiliateName={affiliateName}
          urlTarget={linkoutTarget}
          isMobile={isMobile}
        />
        {isTabbed && getTabs()}
        {!isTabbed && logoImageURL && (
          <PartnerLogo
            logoUrl={logoImageURL}
            partnerLabel={partnerLabel}
            className={s.partner_logo}
            isMobile={isMobile}
            layout={logoLayout}
          />
        )}
      </div>
      {searchBar && (
        <SearchBar
          useGA4={useGA4}
          {...searchBar}
          className={s.search_container}
        />
      )}
      <div className={listClass}>{finalListContent}</div>
      {viewMore && linkout && (
        <div className="t_cntr">
          <MoreLink
            link={linkout}
            text="View More"
            useGA4={useGA4}
            ga4={ga4}
            ga={ga}
            linkoutTarget={linkoutTarget}
          />
        </div>
      )}
      {randomizeData &&
        randomizeData.CTA &&
        randomizeData.CTA.icon === 'shuffle' && (
          <div className="t_cntr">
            <MoreLink
              text={randomizeData.CTA.text || 'shuffle'}
              ga={randomizeData.CTA.ga || ga}
              icon={randomizeData.CTA.icon}
              useGA4={useGA4}
              ga4={ga4}
              clickHandler={() => setCategoryItems(shuffleArray(finalItems))}
              layout="withBG"
            />
          </div>
        )}
    </div>
  )
}

CategoryListV2.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.shape({
      text: PropTypes.string,
      type: PropTypes.string,
    }),
    linkout: PropTypes.string,
    linkoutTarget: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }),
  config: PropTypes.shape({
    logo: PropTypes.shape({
      text: PropTypes.string,
      imageURL: PropTypes.string,
      layout: PropTypes.string,
    }),
    ga: PropTypes.shape({
      category: PropTypes.string,
      action: PropTypes.string,
    }),
    randomizeData: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})]),
    viewMore: PropTypes.bool,
    cardLayout: PropTypes.string,
    cardHeight: PropTypes.number,
    layout: PropTypes.oneOf(supportedLayouts),
    className: PropTypes.string,
    headerBgTheme: PropTypes.string,
    searchBar: PropTypes.shape({}),
    intermediateURLConfig: PropTypes.shape({}),
    validAffiliateList: PropTypes.shape({}),
    ctaText: PropTypes.string,
    noDeals: PropTypes.bool,
    ga4: PropTypes.shape({}),
    affiliateName: PropTypes.string,
    useGA4: PropTypes.bool,
  }),
  isMobile: PropTypes.bool,
  noShowCallback: PropTypes.func,
}
CategoryListV2.defaultProps = {
  data: {
    heading: {},
  },
  config: {
    ga: {
      category: '',
      action: '',
    },
    ga4: {},
    affiliateName: '',
    useGA4: false,
    searchBar: undefined,
    intermediateURLConfig: {},
    validAffiliateList: {},
    noDeals: false,
  },
  isMobile: false,
  noShowCallback: () => {},
}

export default CategoryListV2
